import { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import {
  NewChip,
  SimpleHelpText,
  Header,
  NavigateBeforeButton,
} from "@components";
import {
  useTemplates,
  usePopulateRequestWithTemplate,
  useRequestData,
} from "@hooks";
import { Template, TemplateType } from "@interfaces";
import { buildTemplateDescription } from "../utils";

import {
  Container as StyledContainer,
  EditIcon,
  EmptyStateText,
  FullScreenContainer,
  FullScreenTemplatesList,
  HeaderRow,
  InfoIcon,
  LoadingContainer,
  LoadingOverlay,
  NewTemplateButton,
  TemplateDescription,
  TemplateItem,
  TemplateItemContent,
  TemplateName,
  TemplatesList,
  Title,
  TitleContainer,
  ViewAllButton,
} from "./TemplatesSection.styles";

function TruncatedTemplateName({ name }: { name: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [name]);

  return (
    <Tooltip
      title={isTruncated ? name : ""}
      disableHoverListener={!isTruncated}
    >
      <TemplateName ref={ref}>{name}</TemplateName>
    </Tooltip>
  );
}

interface TemplatesSectionProps {
  maxItems?: number;
}

export function TemplatesSection({ maxItems }: TemplatesSectionProps) {
  const navigate = useNavigate();
  const { requestId = "" } = useParams();

  const { data: requestData } = useRequestData();
  const { data: templates, isLoading } = useTemplates({
    templateType: TemplateType.PRESCRIPTION,
  });

  const [showAllTemplates, setShowAllTemplates] = useState(false);

  const { mutate: populateRequest, isPending: isApplying } =
    usePopulateRequestWithTemplate();

  const hasMoreTemplates =
    maxItems !== undefined &&
    templates !== undefined &&
    templates.length > maxItems;
  const displayedTemplates =
    maxItems !== undefined && templates && !showAllTemplates
      ? templates.slice(0, maxItems)
      : templates;

  const handleTemplateClick = (template: Template) => {
    if (!requestData || isApplying) return;

    populateRequest(
      {
        requestId,
        templateId: template.id,
        patientUuid: requestData.request.patientUuid,
      },
      {
        onSuccess: () => {
          navigate(`/app/solicitudes/${requestId}/receta/crear`, {
            state: { fromTemplate: true, detailAdded: true },
          });
        },
      },
    );
  };

  const handleEditTemplateClick = (e: React.MouseEvent, template: Template) => {
    e.stopPropagation();
    navigate(`/app/solicitudes/${requestId}/plantilla/${template.id}/editar`);
  };

  const handleNewTemplateClick = () => {
    navigate(
      `/app/solicitudes/${requestId}/plantilla/crear/tratamiento/buscar`,
    );
  };

  if (isApplying) {
    return (
      <LoadingOverlay>
        <CircularProgress size={48} />
      </LoadingOverlay>
    );
  }

  return (
    <StyledContainer>
      <HeaderRow>
        <TitleContainer>
          <Title>Plantillas</Title>
          <NewChip />
        </TitleContainer>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {hasMoreTemplates && (
            <ViewAllButton onClick={() => setShowAllTemplates(true)}>
              Ver todas
            </ViewAllButton>
          )}
          <SimpleHelpText text="Crea plantillas de tus tratamientos más frecuentes para recetar más rápido y usarlas con todos tus pacientes." />
        </Box>
      </HeaderRow>

      <NewTemplateButton onClick={handleNewTemplateClick}>
        Nueva plantilla
      </NewTemplateButton>

      {isLoading ? (
        <LoadingContainer>
          <CircularProgress size={32} />
        </LoadingContainer>
      ) : displayedTemplates && displayedTemplates.length > 0 ? (
        <TemplatesList>
          {displayedTemplates.map((template) => (
            <TemplateItem
              key={template.id}
              onClick={() => handleTemplateClick(template)}
            >
              <TemplateItemContent>
                <TruncatedTemplateName name={template.name} />
                {(() => {
                  const desc = buildTemplateDescription(template);
                  return desc ? (
                    <TemplateDescription>{desc}</TemplateDescription>
                  ) : null;
                })()}
              </TemplateItemContent>
              <IconButton
                size="small"
                onClick={(e) => handleEditTemplateClick(e, template)}
              >
                <EditIcon />
              </IconButton>
            </TemplateItem>
          ))}
        </TemplatesList>
      ) : (
        <EmptyStateText>
          <InfoIcon />
          <Typography variant="body1" sx={{ marginLeft: "8px" }}>
            No tienes plantillas guardadas.
          </Typography>
        </EmptyStateText>
      )}

      {showAllTemplates && templates && (
        <FullScreenContainer>
          <Header
            title="Plantillas"
            titleColor="black"
            left={
              <NavigateBeforeButton
                onClick={() => setShowAllTemplates(false)}
              />
            }
          />
          <FullScreenTemplatesList>
            <Box sx={{ padding: 2 }}>
              <NewTemplateButton onClick={handleNewTemplateClick}>
                Nueva plantilla
              </NewTemplateButton>
            </Box>
            {templates.map((template) => (
              <TemplateItem
                key={template.id}
                onClick={() => handleTemplateClick(template)}
              >
                <TemplateItemContent>
                  <TruncatedTemplateName name={template.name} />
                  {(() => {
                    const desc = buildTemplateDescription(template);
                    return desc ? (
                      <TemplateDescription>{desc}</TemplateDescription>
                    ) : null;
                  })()}
                </TemplateItemContent>
                <IconButton
                  size="small"
                  onClick={(e) => handleEditTemplateClick(e, template)}
                >
                  <EditIcon />
                </IconButton>
              </TemplateItem>
            ))}
          </FullScreenTemplatesList>
        </FullScreenContainer>
      )}
    </StyledContainer>
  );
}
